/**
 * كتالوج علاقات الموظفين.
 *
 * حالةٌ تُفتح ويُسمع فيها وتُقرَّر ثمّ تُغلق، وإجراءٌ تأديبيٌّ يُسجَّل بشدّته
 * ومدّةِ سريانه، ورضًا يُقاس بلا أسماء.
 *
 * ## ولماذا سجلُّ الحالة لا يُحذف منه شيء
 *
 * من يقرؤه بعد سنتين — في تظلُّمٍ أو نزاعٍ قضائيّ — يحتاج أن يرى ما سُمع
 * ومتى وممّن. وملفٌّ تُمحى منه واقعةٌ لا يصلح سندًا لأحد: لا للشركة ولا
 * للموظّف.
 *
 * ## وما لا يفعله هذا الباب
 *
 * لا يُنهي خدمةَ أحدٍ ولا يخصم من أحدٍ تلقائيًّا. الإجراءُ قرارٌ يُسجَّل،
 * والخصمُ يصير مالًا بسطرٍ في المرتّب يُكتب بضغطةٍ صريحةٍ وتُقرأ في
 * القسيمة. وإنهاءُ الخدمة يقع في ملفّ الموظّف ويُوقَّع عليه.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   نوعُ الحالة
   ========================================================================== */

export const CASE_KINDS = [
  'GRIEVANCE',
  'COMPLAINT',
  'DISPUTE',
  'MISCONDUCT',
] as const;
export type CaseKind = (typeof CASE_KINDS)[number];

export interface CaseKindDef extends Labelled<CaseKind> {
  description: string;
  /** هل يجوز أن يُبنى عليها إجراءٌ تأديبيّ. */
  disciplinary: boolean;
  /** هل تلزمها جهةٌ تُنسب إليها. */
  needsSubject: boolean;
}

export const CASE_KIND_DEFS: readonly CaseKindDef[] = [
  {
    key: 'GRIEVANCE',
    label: 'تظلُّم',
    kind: 'info',
    description: 'اعتراضٌ على قرارٍ يخصّه: خصمٌ أو نقلٌ أو تقييم.',
    disciplinary: false,
    needsSubject: false,
  },
  {
    key: 'COMPLAINT',
    label: 'شكوى',
    kind: 'warning',
    description: 'شكوى من سلوكِ زميلٍ أو مديرٍ أو من بيئة العمل.',
    disciplinary: false,
    needsSubject: false,
  },
  {
    key: 'DISPUTE',
    label: 'نزاع',
    kind: 'warning',
    description: 'خلافٌ بين طرفين يُطلب فضُّه.',
    disciplinary: false,
    needsSubject: false,
  },
  {
    key: 'MISCONDUCT',
    label: 'مخالفة',
    kind: 'danger',
    description: 'مخالفةٌ منسوبةٌ إلى موظّف — وهذه وحدَها يُبنى عليها إجراء.',
    disciplinary: true,
    needsSubject: true,
  },
] as const;

export const CASE_KIND_MAP = Object.fromEntries(
  CASE_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CaseKind, CaseKindDef>>;

/* ==========================================================================
   حالةُ الحالة
   ========================================================================== */

export const CASE_STATUSES = [
  'NEW',
  'INVESTIGATING',
  'DECIDED',
  'CLOSED',
  'WITHDRAWN',
] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

export interface CaseStatusDef extends Labelled<CaseStatus> {
  description: string;
  /** هل ما زالت تنتظر عملًا. */
  live: boolean;
  /** هل يُكتب في سجلّها. */
  writable: boolean;
}

export const CASE_STATUS_DEFS: readonly CaseStatusDef[] = [
  {
    key: 'NEW',
    label: 'جديدة',
    kind: 'warning',
    description: 'فُتحت ولم يُنظر فيها بعد.',
    live: true,
    writable: true,
  },
  {
    key: 'INVESTIGATING',
    label: 'قيد التحقيق',
    kind: 'info',
    description: 'يُسمع أطرافُها وتُجمع بيّناتُها.',
    live: true,
    writable: true,
  },
  {
    key: 'DECIDED',
    label: 'صدر فيها قرار',
    kind: 'info',
    description: 'قُرِّر فيها وينتظر ما يُبنى عليه.',
    live: true,
    writable: true,
  },
  {
    key: 'CLOSED',
    label: 'مغلقة',
    kind: 'success',
    description: 'انتهت وانتهى ما بُني عليها — وسجلُّها يبقى.',
    live: false,
    writable: false,
  },
  {
    key: 'WITHDRAWN',
    label: 'سُحبت',
    kind: 'neutral',
    description: 'سحبها صاحبُها — ولا تُقرأ حكمًا على أحد.',
    live: false,
    writable: false,
  },
] as const;

export const CASE_STATUS_MAP = Object.fromEntries(
  CASE_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CaseStatus, CaseStatusDef>>;

/**
 * ما يجوز أن تصير إليه الحالةُ من حالتها.
 *
 * والمغلقةُ والمسحوبةُ طرفان: ما أُغلق لا يُعاد فتحُه — ومن جدّ فيه جديدٌ
 * تُفتح له حالةٌ جديدةٌ تشير إلى الأولى. وإعادةُ الفتح تعني أن يُعدَّل قرارٌ
 * أُبلغ به أطرافُه من ورائهم.
 */
export const CASE_NEXT: Readonly<Record<CaseStatus, readonly CaseStatus[]>> = {
  NEW: ['INVESTIGATING', 'DECIDED', 'WITHDRAWN'],
  INVESTIGATING: ['DECIDED', 'WITHDRAWN'],
  DECIDED: ['CLOSED'],
  CLOSED: [],
  WITHDRAWN: [],
};

/* ==========================================================================
   وقائعُ السجلّ
   ========================================================================== */

export const CASE_EVENT_KINDS = [
  'NOTE',
  'INTERVIEW',
  'EVIDENCE',
  'DECISION',
  'STATUS',
] as const;
export type CaseEventKindKey = (typeof CASE_EVENT_KINDS)[number];

export interface CaseEventKindDef extends Labelled<CaseEventKindKey> {
  description: string;
  /** هل يكتبها المستخدمُ بيده أم يكتبها النظامُ عن واقعة. */
  manual: boolean;
}

export const CASE_EVENT_KIND_DEFS: readonly CaseEventKindDef[] = [
  {
    key: 'NOTE',
    label: 'ملاحظة',
    kind: 'neutral',
    description: 'ما يُكتب عن الحالة.',
    manual: true,
  },
  {
    key: 'INTERVIEW',
    label: 'سماعُ طرف',
    kind: 'info',
    description: 'ما قاله طرفٌ أو شاهدٌ حين سُمع.',
    manual: true,
  },
  {
    key: 'EVIDENCE',
    label: 'بيّنة',
    kind: 'info',
    description: 'رسالةٌ أو صورةٌ أو مستندٌ أُودع.',
    manual: true,
  },
  {
    key: 'DECISION',
    label: 'قرار',
    kind: 'success',
    description: 'ما قُرِّر ولماذا.',
    manual: false,
  },
  {
    key: 'STATUS',
    label: 'تغيّرت الحالة',
    kind: 'neutral',
    description: 'يكتبه النظامُ عند كلّ انتقال.',
    manual: false,
  },
] as const;

export const CASE_EVENT_KIND_MAP = Object.fromEntries(
  CASE_EVENT_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CaseEventKindKey, CaseEventKindDef>>;

/* ==========================================================================
   الإجراءات التأديبية
   ========================================================================== */

export const DISCIPLINE_KINDS = [
  'VERBAL',
  'WRITTEN',
  'FINAL_WARNING',
  'SUSPENSION',
  'DEDUCTION',
  'TERMINATION',
] as const;
export type DisciplineKindKey = (typeof DISCIPLINE_KINDS)[number];

export interface DisciplineKindDef extends Labelled<DisciplineKindKey> {
  description: string;
  /** ترتيبُ الشدّة — واحدٌ أخفُّها. */
  severity: number;
  /** هل يلزمها مبلغ. */
  needsAmount: boolean;
  /** هل تلزمها أيّام. */
  needsDays: boolean;
  /** ما يُفعل خارجَ هذا الباب لينفَّذ، إن كان. */
  outsidePath: string | null;
  outsideLabel: string | null;
}

export const DISCIPLINE_KIND_DEFS: readonly DisciplineKindDef[] = [
  {
    key: 'VERBAL',
    label: 'تنبيه شفهيّ',
    kind: 'neutral',
    description: 'قيل له مشافهةً وسُجّل أنّه قيل.',
    severity: 1,
    needsAmount: false,
    needsDays: false,
    outsidePath: null,
    outsideLabel: null,
  },
  {
    key: 'WRITTEN',
    label: 'إنذار كتابيّ',
    kind: 'warning',
    description: 'إنذارٌ يُوقَّع عليه ويبقى في ملفّه مدّةً.',
    severity: 2,
    needsAmount: false,
    needsDays: false,
    outsidePath: null,
    outsideLabel: null,
  },
  {
    key: 'FINAL_WARNING',
    label: 'إنذار نهائيّ',
    kind: 'danger',
    description: 'آخرُ ما قبل إنهاء الخدمة.',
    severity: 3,
    needsAmount: false,
    needsDays: false,
    outsidePath: null,
    outsideLabel: null,
  },
  {
    key: 'SUSPENSION',
    label: 'وقف عن العمل',
    kind: 'danger',
    description: 'يُوقَف مدّةً معلومة.',
    severity: 4,
    needsAmount: false,
    needsDays: true,
    outsidePath: null,
    outsideLabel: null,
  },
  {
    key: 'DEDUCTION',
    label: 'خصم من الأجر',
    kind: 'danger',
    description: 'مبلغٌ يُخصم — ولا يصير مالًا حتى يُكتب في المرتّب.',
    severity: 5,
    needsAmount: true,
    needsDays: false,
    outsidePath: '/hr/payroll',
    outsideLabel: 'يُنفَّذ بسطرٍ في المرتّب',
  },
  {
    key: 'TERMINATION',
    label: 'إنهاء خدمة',
    kind: 'danger',
    description: 'أشدُّها — ويقع في ملفّ الموظّف لا من هنا.',
    severity: 6,
    needsAmount: false,
    needsDays: false,
    outsidePath: '/hr/employees',
    outsideLabel: 'يُنفَّذ في ملفّ الموظّف',
  },
] as const;

export const DISCIPLINE_KIND_MAP = Object.fromEntries(
  DISCIPLINE_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<DisciplineKindKey, DisciplineKindDef>>;

/**
 * الإنذارُ يسقط بمضيّ مدّته.
 *
 * إنذارٌ يبقى في الملفّ إلى الأبد ليس إنذارًا — هو حكمٌ مؤبَّد. والمدّةُ
 * تُكتب عند الإصدار، وهذه هي المقترَحة حين لا تُكتب.
 */
export const DEFAULT_WARNING_MONTHS = 12;

/** هل سقط أثرُ الإجراء بمضيّ مدّته. */
export function disciplineLapsed(expiresOn: string | null): boolean {
  if (!expiresOn) return false;
  return new Date(`${expiresOn}T00:00:00.000Z`).getTime() < Date.now();
}

/* ==========================================================================
   قياسُ الرضا
   ========================================================================== */

export interface PulseQuestion {
  key: string;
  text: string;
}

export const MAX_PULSE_QUESTIONS = 15;
export const PULSE_KEY_PATTERN = /^[A-Z][A-Z0-9_]{1,29}$/;
export const PULSE_SCALE = 5;

/**
 * أسئلةٌ تُقترَح على استبيانٍ جديد.
 *
 * تُحذف وتُستبدل — وبديلُها صفحةٌ فارغةٌ يقف أمامها من لم يكتب استبيانَ
 * رضًا قطّ. والأسئلةُ الخمسةُ هذه تُقاس بها الاتّجاهاتُ الأشهرُ في الأدبيّات:
 * العملُ نفسُه، والمدير، والتقدير، والنموّ، والبقاء.
 */
export const SUGGESTED_PULSE: readonly PulseQuestion[] = [
  { key: 'WORK', text: 'عملي واضحٌ وأعرف ما يُنتظر منّي.' },
  { key: 'MANAGER', text: 'مديري يسمعني ويعطيني ملاحظاتٍ مفيدة.' },
  { key: 'RECOGNITION', text: 'أشعر أنّ ما أُنجزه يُلاحَظ ويُقدَّر.' },
  { key: 'GROWTH', text: 'أرى لي مسارًا أنمو فيه هنا.' },
  { key: 'STAY', text: 'أرى نفسي في هذه الشركة بعد سنة.' },
] as const;

export function readPulseQuestions(raw: unknown): PulseQuestion[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: PulseQuestion[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const key = typeof row.key === 'string' ? row.key : '';
    const text = typeof row.text === 'string' ? row.text.trim() : '';
    if (!PULSE_KEY_PATTERN.test(key) || !text) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, text });
    if (out.length >= MAX_PULSE_QUESTIONS) break;
  }
  return out;
}

export function readPulseScores(
  raw: unknown,
  questions: readonly PulseQuestion[],
): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  const row = raw as Record<string, unknown>;
  const out: Record<string, number> = {};
  for (const q of questions) {
    const value = row[q.key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    if (value < 1 || value > PULSE_SCALE) continue;
    out[q.key] = Math.round(value);
  }
  return out;
}

/**
 * أقلُّ عددِ إجاباتٍ يُنشر عنه رقمٌ — للاستبيان كلِّه أو لقسمٍ فيه.
 *
 * الرضا يُقاس بلا أسماء، والعتبةُ هي ما يجعل ذلك صحيحًا: متوسّطُ قسمٍ فيه
 * ثلاثةٌ اسمٌ لثلاثة، ومن يعرف عددَهم يعرف من قال ماذا. وبلا عتبةٍ يُقرأ
 * الاستبيانُ مرّةً ثمّ لا يُجيب عليه أحدٌ بصدقٍ بعدها.
 */
export const MIN_PULSE_SAMPLE = 5;

/** مرتبةُ متوسّطٍ على سُلَّم الخمسة — تُقرأ كلمةً لا رقمًا وحده. */
export function pulseBand(average: number | null): {
  label: string;
  kind: 'success' | 'info' | 'warning' | 'danger';
} | null {
  if (average === null) return null;
  if (average >= 4.2) return { label: 'مرتفع', kind: 'success' };
  if (average >= 3.5) return { label: 'جيّد', kind: 'info' };
  if (average >= 2.8) return { label: 'متوسّط', kind: 'warning' };
  return { label: 'منخفض', kind: 'danger' };
}
